/**
 * Orders parked to one side.
 *
 * WHY A TILL NEEDS THIS
 * ---------------------
 * A customer is halfway through ordering and goes to fetch a friend, or wants
 * to check something, and there is a queue behind them. Without somewhere to
 * put that order the cashier either makes the queue wait or throws the order
 * away and rings it again from memory.
 *
 * Nothing is sold when an order is parked — no stock moves, no money is taken,
 * no number is issued. It is a basket in a drawer, and it expires after two
 * hours so a forgotten one does not sit there until closing.
 */
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

export function HeldOrdersSheet({ held, loading, onRecall, onClose, busy }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Parked orders"
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/70 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface p-5">
        <h2 className="font-semibold text-lg text-ink">Parked orders</h2>
        <p className="mt-0.5 text-sm text-ink-2">
          Nothing has been sold. These clear themselves after two hours.
        </p>

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner label="Loading parked orders" />
          </div>
        ) : held.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-3">Nothing parked.</p>
        ) : (
          <ul className="mt-4 max-h-80 space-y-2 overflow-y-auto">
            {held.map((order) => {
              const basket = parseBasket(order.order_data);

              return (
                <li key={order.id}>
                  <button
                    onClick={() => onRecall(order.id)}
                    disabled={busy}
                    className="flex w-full items-center gap-3 rounded-lg border border-rule p-3 text-left hover:border-brand-400 hover:bg-brand-400/10 disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-ink">
                        {order.customer_name || 'No name'}
                      </span>
                      <span className="block text-sm text-ink-2">
                        {basket.count} item{basket.count === 1 ? '' : 's'}
                        {order.reason && ` · ${order.reason}`}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-medium text-brand-700">Bring back</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <Button variant="secondary" size="lg" className="mt-5 w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/**
 * How many items are in a parked basket.
 *
 * The basket is stored as text and comes back as text. A parked order from an
 * older version of the till, or one that was somehow written badly, must not
 * take the whole list down with it — the other parked orders still need
 * bringing back.
 */
function parseBasket(raw) {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const items = data?.items ?? [];
    return { count: items.reduce((n, i) => n + (Number(i.quantity) || 0), 0) };
  } catch {
    return { count: 0 };
  }
}
